// ============================================================================
// LA MODALE DE TRAVAIL — formulaire, fiche, parcours.
//
// ⚠️ ELLE N'EST PAS LE DIALOGUE DE CONFIRMATION (`confirm`/`alert`, déjà dans ce paquet). Deux canaux
// distincts : celui-ci se ferme pour aller lire l'écran, l'autre INTERROMPT et attend une réponse.
// Un dialogue bloquant peut recouvrir une modale de travail — c'est pour ça que son voile est plus
// clair : il doit laisser lire ce qu'il recouvre pendant qu'on tranche.
//
// ⚠️ SON COMPORTEMENT CLAVIER VIT DANS `overlay.ts` (focus qui entre et revient, tabulation enfermée,
// Échap, verrou de défilement) : le tiroir latéral en a besoin à l'identique, et deux copies d'un
// piège à tabulation divergent sans que rien ne le montre à l'écran. Ce fichier ne garde que ce qui
// fait une MODALE — un panneau centré, son voile et son anatomie.
// ============================================================================
import { useId } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import {
  MODAL_ANIM, MODAL_BAND, MODAL_BODY, MODAL_CARD, MODAL_CLOSE, MODAL_FOOT, MODAL_HEADER, MODAL_HEIGHT,
  MODAL_SCRIM, MODAL_TILE_TONES, MODAL_WIDTHS, Z_LAYERS,
  type ModalDensity, type ModalTone, type ModalWidth,
  type IconName,
} from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { PlanDeSurcouche, useOverlayPanel, type OverlayPanelOptions } from './overlay';
import { t } from '@orgazone/i18n';

/** ⚠️ `open`, `onClose`, `autoFocus` et `blockClose` viennent du socle : une modale EST un panneau superposé. */
export interface ModalProps extends OverlayPanelOptions {
  /** Titre — obligatoire : il nomme le panneau pour le lecteur d'écran autant que pour l'œil. */
  title: string;
  subtitle?: string;
  /** Icône de tuile. Le TON dit le domaine (catalogue, import, destructif…), jamais l'humeur. */
  icon?: IconName;
  tone?: ModalTone;
  width?: ModalWidth;
  density?: ModalDensity;
  /** Hauteur FIGÉE : obligatoire dès qu'il y a plusieurs étapes, sinon le pied se déplace sous le doigt. */
  capHeight?: keyof typeof MODAL_HEIGHT.caps;
  /**
   * BANDE DE MÉTA sous le titre (code, identifiants, état) — dans l'en-tête FIXE.
   * ⚠️ Elle identifie le SUJET du panneau : rendue dans le corps, elle défile et disparaît au premier
   * coup de molette, exactement quand on en a besoin pour savoir ce qu'on est en train de lire.
   */
  meta?: ReactNode;
  /**
   * ACTIONS DE L'EN-TÊTE, posées juste avant la croix (« Dupliquer » sur la fiche article).
   * ⚠️ RÉSERVÉ À CE QUI AGIT SUR LE SUJET ENTIER du panneau : un geste qui touche un onglet vit dans cet onglet, et
   *    l'enregistrement reste au pied.
   */
  actions?: ReactNode;
  /**
   * BANDEAU DE L'EN-TÊTE, sous la bande de méta et au-dessus des onglets — un avis qui porte sur le sujet entier (« Copie
   * de … — vérifiez les couleurs et les prix »). ⚠️ Dans l'en-tête et non dans le corps : il ne défile pas avec l'onglet.
   */
  notice?: ReactNode;
  /**
   * BARRE D'ONGLETS — DERNIER enfant de l'en-tête, jamais du corps.
   * ⚠️ Deux raisons, et aucune n'est esthétique : posée dans le corps elle DÉFILE (on perd de vue la
   * section où l'on est), et le trait de 2 px de l'onglet actif doit venir se poser SUR le filet du bas
   * de l'en-tête — d'où le rembourrage bas à zéro dès qu'elle est là.
   */
  tabs?: ReactNode;
  /**
   * BANDE FIXE entre l'en-tête et le corps — le fil d'étapes d'un assistant (`Steps` en habillage `bare`).
   * ⚠️ Elle ne défile pas : posé dans le corps, le fil disparaît dès qu'une étape est plus haute que la fenêtre.
   */
  band?: ReactNode;
  /**
   * EN-TÊTE « HÉROS » : fond teinté de marque et filet de marque, comme la fiche article.
   * ⚠️ Réservé aux panneaux qui présentent un OBJET (une fiche). Un parcours garde l'en-tête neutre :
   * teinter les deux ferait de la couleur une décoration au lieu d'un signe.
   */
  hero?: boolean;
  children: ReactNode;
  /** Pied : boutons d'action. Absent = pas de pied (la modale se ferme par la croix ou Échap). */
  footer?: ReactNode;
  /**
   * Le clic sur le VOILE ferme-t-il ? Défaut `true`.
   *
   * ⚠️ À POSER SUR LES PANNEAUX DE TRAVAIL (décision utilisateur 2026-08-25). Sur une petite modale
   * qui pose une question, le clic à côté est la sortie naturelle. Sur une fiche — plusieurs onglets,
   * des champs, une position dans la page — c'est un geste qu'on ne fait jamais exprès : viser une
   * pastille, rater de trois pixels, et tout se referme. Le voile occupe la plus grande partie de
   * l'écran, donc la cible la plus facile à toucher est aussi celle qu'on veut le moins.
   *
   * ⚠️ ÉCHAP RESTE UNE SORTIE, et ce n'est pas une demi-mesure : c'est une frappe DÉLIBÉRÉE, pas un
   * clic manqué, et c'est la seule sortie au clavier d'un panneau modal. La retirer rendrait la fiche
   * inutilisable sans souris.
   */
  closeOnBackdrop?: boolean;
}

export function Modal({
  open, onClose, title, subtitle, icon, tone = 'ink', autoFocus = 'first',
  width = 'md', density = 'ample', capHeight, meta, actions, notice, tabs, band, hero, children, footer, blockClose,
  closeOnBackdrop = true,
}: ModalProps) {
  const { panelRef, close } = useOverlayPanel({ open, onClose, autoFocus, blockClose });
  const titleId = useId();
  const subId = useId();

  if (!open) return null;

  const h = density === 'fiche' ? MODAL_HEADER.record : MODAL_HEADER.ample;
  // ⚠️ Pas `t` : ce nom est celui du moteur de textes (@orgazone/i18n), employé dans ce fichier.
  const tile = MODAL_TILE_TONES[tone];
  const cap = capHeight ? MODAL_HEIGHT.caps[capHeight] : undefined;

  const carte: CSSProperties = {
    width: MODAL_WIDTHS[width], maxWidth: `${MODAL_CARD.maxWidthVw}vw`,
    maxHeight: cap ? `min(${MODAL_HEIGHT.maxVh}vh, ${cap}px)` : `${MODAL_HEIGHT.maxVh}vh`,
    height: cap ? `min(${MODAL_HEIGHT.maxVh}vh, ${cap}px)` : undefined,
    display: 'flex', flexDirection: 'column', minHeight: 0,
    background: 'var(--paper)', borderRadius: `var(--r-${MODAL_CARD.radius})`,
    boxShadow: `var(--sh-${MODAL_CARD.shadow})`, overflow: 'hidden',
    // ⚠️ `modal-pop` et non `pop` : un panneau centré n'a pas de déclencheur d'où sortir — il grandit
    // sur place. `pop` le ferait glisser depuis nulle part.
    animation: `modal-pop ${MODAL_ANIM.enterMs}ms ${MODAL_ANIM.easing}`,
  };

  return (
    <ModalBackdrop onBackdrop={closeOnBackdrop ? close : undefined}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        className="oz-modal"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subId : undefined}
        tabIndex={-1}
        style={carte}
      >
        {/* ⚠️ LISERÉ DE TÊTE : le SEUL endroit où le panneau touche la couleur du produit. Sans lui,
            l'en-tête et le corps sont deux surfaces claires empilées et la carte n'a pas de haut. */}
        <div aria-hidden style={{
          height: MODAL_CARD.topRule, flexShrink: 0,
          background: 'linear-gradient(90deg, var(--teal-500), var(--teal-300))',
        }} />
        <header style={{
          display: 'flex', flexDirection: 'column',
          // ⚠️ ZÉRO EN BAS DÈS QU'IL Y A DES ONGLETS : c'est la barre qui touche le filet de l'en-tête, et
          // le trait de l'onglet actif vient se poser dessus. Un rembourrage bas casserait l'emboîtement.
          padding: `${h.padV}px ${h.padH}px ${tabs ? 0 : h.padV}px`,
          background: hero ? 'var(--hero)' : 'var(--paper)',
          borderBottom: `1px solid var(--${hero ? 'hero-line' : MODAL_HEADER.border})`,
          flexShrink: 0,
        }}>
          {/* ⚠️ ALIGNÉE EN HAUT, jamais centrée : la tuile déborde SOUS le titre, à hauteur de la bande
              de méta. Centrée, elle repousserait le titre au milieu d'un en-tête à deux lignes. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: h.gap }}>
            {icon && (
              <span style={{
                width: h.tile, height: h.tile, borderRadius: `var(--r-${MODAL_HEADER.tileRadius})`,
                // ⚠️ DU PAPIER BORDÉ, pas un aplat de couleur : seule l'ICÔNE porte la teinte.
                background: `var(--${MODAL_HEADER.tileBg})`,
                border: `1px solid var(--${MODAL_HEADER.tileBorder})`,
                color: `var(--${tile.fg})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon name={icon} size={h.icon} stroke={h.iconStroke} />
              </span>
            )}
            <span style={{ flex: 1, minWidth: 0 }}>
              {/* ⚠️ TRONQUÉ : une désignation longue passait à la ligne et poussait tout l'en-tête. */}
              <h2 id={titleId} style={{
                margin: 0, fontSize: h.titleSize, fontWeight: MODAL_HEADER.titleWeight,
                letterSpacing: MODAL_HEADER.titleTracking, color: 'var(--ink-900)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{title}</h2>
              {subtitle && (
                <p id={subId} style={{ margin: '3px 0 0', fontSize: h.subSize, color: 'var(--ink-500)', lineHeight: 1.5 }}>
                  {subtitle}
                </p>
              )}
              {meta && (
                <div style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                  gap: MODAL_HEADER.metaGap, marginTop: MODAL_HEADER.metaTop,
                }}>{meta}</div>
              )}
            </span>
            {actions && <div style={{ display: 'flex', alignItems: 'center', gap: MODAL_HEADER.metaGap, flexShrink: 0 }}>{actions}</div>}
            <button
              type="button"
              className="oz-modal-x"
              onClick={close}
              aria-label={t('Fermer')}
              style={{
                width: MODAL_CLOSE.size, height: MODAL_CLOSE.size,
                borderRadius: `var(--r-${MODAL_CLOSE.radius})`, border: 'none',
                background: `var(--${MODAL_CLOSE.bg})`, color: `var(--${MODAL_CLOSE.fg})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, cursor: 'pointer',
              }}
            >
              <Icon name="x" size={MODAL_CLOSE.icon} stroke={MODAL_CLOSE.iconStroke} />
            </button>
          </div>
          {notice && <div style={{ marginTop: MODAL_HEADER.noticeTop }}>{notice}</div>}
          {tabs && <div style={{ marginTop: MODAL_HEADER.tabsTop }}>{tabs}</div>}
        </header>

        {band && (
          <div style={{
            flexShrink: 0, padding: `${MODAL_BAND.padV}px ${MODAL_BAND.padH}px`,
            background: `var(--${MODAL_BAND.bg})`, borderBottom: `1px solid var(--${MODAL_BAND.border})`,
          }}>{band}</div>
        )}

        {/* ⚠️ `minHeight: 0` est STRUCTUREL : sans lui, ce corps pousse le pied hors du panneau — et
            c'est le pied qui porte la validation. */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: `${MODAL_BODY.padV}px ${MODAL_BODY.padH}px`,
        }}>
          {children}
        </div>

        {footer && (
          <footer style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: MODAL_FOOT.gap,
            padding: `${MODAL_FOOT.padV}px ${MODAL_FOOT.padH}px`,
            borderTop: `1px solid var(--${MODAL_FOOT.border})`,
            background: `var(--${MODAL_FOOT.bg})`, flexShrink: 0,
          }}>
            {footer}
          </footer>
        )}
      </div>
    </ModalBackdrop>
  );
}

/**
 * LE VOILE D'UNE MODALE, SEUL — le portail, le plan de surcouche et l'encre DILUÉE.
 *
 * UTILITÉ : un panneau de travail que l'anatomie de `Modal` ne couvre pas (l'éditeur de commandes, à 90 %
 * de l'écran, quand `Modal` plafonne à 920 px) pose son panneau DANS ce voile au lieu d'en peindre un :
 * le voile recopié à la main a déjà recouvert une page d'un aplat noir (encre non diluée). Le clavier
 * (Échap, focus, tabulation) reste à `useOverlayPanel`, que le panneau appelle lui-même.
 */
export function ModalBackdrop({ onBackdrop, children }: {
  /** Fermer au clic sur le VOILE — absent sur un panneau de travail, où ce clic n'est jamais voulu. */
  onBackdrop?: () => void;
  children: ReactNode;
}) {
  return createPortal(
    <PlanDeSurcouche.Provider value={Z_LAYERS.modal}>
      <div
        // Le voile ferme au clic — mais seulement sur LUI, pas sur un clic relâché depuis le panneau.
        onMouseDown={(e) => { if (onBackdrop && e.target === e.currentTarget) onBackdrop(); }}
        style={{
          position: 'fixed', inset: 0, zIndex: MODAL_SCRIM.z,
          // ⚠️ UNE ENCRE, PAS LA SURFACE DE LA PAGE : dérivé de `--canvas`, le voile était une BRUME
          // BLANCHE en thème clair — il éclaircissait ce qu'il devait enfoncer.
          background: `color-mix(in srgb, var(--scrim) ${MODAL_SCRIM.alpha * 100}%, transparent)`,
          backdropFilter: `blur(${MODAL_SCRIM.blur}px)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: MODAL_SCRIM.pad,
        }}
      >
        {children}
      </div>
    </PlanDeSurcouche.Provider>,
    document.body,
  );
}
